/**
 * @fileoverview Where the ticked plants of a card are going, each place once, and the button's words: "Add 4 plants to
 * Back bed", or "Add 4 plants: 3 to Back bed, 1 to Herbs" when some rows have a bed of their own. The several-kinds
 * card (KindsPile) said it this way first; since D78 (rev 2 §2) the packet card and the review sheet do too, a packet
 * row having a bed of its own.
 *
 * Where: KindsPile, PacketPile, PacketReview.
 * API: none.
 * Words (en/es): tray.kinds.newBedNamed, tray.kinds.addSplit, tray.kinds.toBed, packets.add, packets.addOne.
 *
 * @module bedPlaces
 */

import type { BedName } from '@/shared/api/locations';
import type { OwnBed } from './packetRows';

/** A place plants are going, as the button counts them. */
export interface BedPlace {
  /** Tells places apart: "bed:7", or "new:herbs". */
  key: string;
  /** The words for it: the bed's name, or "a new bed, Herbs". */
  label: string;
  /** How many of the ticked plants go there. */
  count: number;
}

/** The translate function the places are said with. */
type Translate = (key: string, values?: Record<string, string | number>) => string;

/**
 * Words lowered and run together, for telling whether two names say the same.
 *
 * @param text - Anything.
 */
const plain = (text: string): string => text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/**
 * Where the ticked plants are going, each place once, in the order they are first met.
 *
 * @param owns - Each ticked plant's own bed, or null for the shared one.
 * @param shared - The shared bed: one of theirs by id, or a new one by name.
 * @param locations - Their live beds.
 * @param t - The translate function.
 * @returns The places.
 */
export function bedPlaces(
  owns: ReadonlyArray<OwnBed | null>,
  shared: { id: number | null; newName?: string | null },
  locations: readonly BedName[],
  t: Translate,
): BedPlace[] {
  const places: BedPlace[] = [];
  for (const own of owns) {
    const bed: OwnBed | null = own ?? (shared.id !== null ? { kind: 'bed', id: shared.id } : shared.newName != null ? { kind: 'new', name: shared.newName } : null);
    const place = bed?.kind === 'new'
      ? { key: `new:${plain(bed.name)}`, label: bed.name.trim() || '…' }
      : { key: `bed:${bed?.id ?? ''}`, label: locations.find((loc) => loc.id === bed?.id)?.name ?? '…' };
    if (bed?.kind === 'new' && own) place.label = t('tray.kinds.newBedNamed', { name: bed.name.trim() || '…' });
    const seen = places.find((each) => each.key === place.key);
    if (seen) seen.count += 1; else places.push({ ...place, count: 1 });
  }
  return places;
}

/**
 * The button's words for the places: one place, "Add 4 plants to Back bed" ("Add 1 plant to Back bed"); several,
 * "Add 4 plants: 3 to Back bed, 1 to Herbs".
 *
 * @param places - Where the plants are going.
 * @param count - How many plants.
 * @param fallback - The shared bed's name, for when nothing is ticked yet.
 * @param t - The translate function.
 * @returns The words.
 */
export function addLabelFor(places: readonly BedPlace[], count: number, fallback: string, t: Translate): string {
  if (places.length > 1) {
    return t('tray.kinds.addSplit', { count, parts: places.map((place) => t('tray.kinds.toBed', { count: place.count, bed: place.label })).join(', ') });
  }
  const bed = places[0]?.label ?? (fallback || '…');
  return count === 1 ? t('packets.addOne', { bed }) : t('packets.add', { count, bed });
}
